<template>
  <footer class="modern-footer">
    <div class="footer-content">
      <v-container>
        <v-row>
          <!-- Company Info -->
          <v-col cols="12" md="4" class="mb-6">
            <div class="footer-section">
              <!-- Back to the home page; on the home page itself, back to the top. -->
              <router-link :to="homeUrl" class="footer-logo mb-4 d-inline-block" @click.native="onLogoClick">
                <app-logo :size="180" class="dark-bg" />
              </router-link>
              <p class="footer-description">
                {{ $t('layout.footer.description') }}
              </p>
              <div class="social-links mt-4">
                <v-btn 
                  v-for="social in socialLinks" 
                  :key="social.name"
                  :href="social.url"
                  target="_blank"
                  icon
                  small
                  class="social-btn mr-2"
                >
                  <v-icon>{{ social.icon }}</v-icon>
                </v-btn>
              </div>
            </div>
          </v-col>

          <!-- Quick Links -->
          <v-col cols="12" sm="6" md="3" class="mb-6">
            <div class="footer-section">
              <h4 class="footer-title">{{ $t('layout.footer.quick-links') }}</h4>
              <ul class="footer-links">
                <li><a href="#" @click.prevent="goToHome">{{ $t('layout.footer.links.home') }}</a></li>
                <!-- Real pages, not a scroll to a home-page section: they work from every page and keep the locale. -->
                <li><router-link :to="localisedPath(mainPath.PRICING)">{{ $t('layout.footer.links.trainers') }}</router-link></li>
                <li><router-link :to="localisedPath(mainPath.PRICING_ORGANIZATION)">{{ $t('layout.footer.links.organizations') }}</router-link></li>
                <li><router-link :to="localisedPath(mainPath.FEATURES)">{{ $t('pages.home-view.features.title') }}</router-link></li>
                <li><router-link :to="localisedPath(mainPath.FAQ)">{{ $t('pages.faq.title') }}</router-link></li>
                <li><a :href="exampleStudioUrl" target="_blank" rel="noopener noreferrer">{{ $t('layout.footer.links.view-in-action') }}</a></li>
              </ul>
            </div>
          </v-col>

          <!-- Legal -->
          <v-col cols="12" sm="6" md="3" class="mb-6">
            <div class="footer-section">
              <h4 class="footer-title">{{ $t('layout.footer.legal') }}</h4>
              <ul class="footer-links">
                <li><router-link to="/privacy">{{ $t('layout.footer.links.privacy') }}</router-link></li>
                <li><router-link to="/terms">{{ $t('layout.footer.links.terms') }}</router-link></li>
                <li><router-link to="/cookies">{{ $t('layout.footer.links.cookies') }}</router-link></li>
                <!-- Change the cookie choice at any time ([B33]). -->
                <li><a href="#" @click.prevent="openCookieSettings">{{ $t('consent.settings-link') }}</a></li>
              </ul>
            </div>
          </v-col>

          <!-- Contact -->
          <v-col cols="12" sm="6" md="2" class="mb-6">
            <div class="footer-section">
              <h4 class="footer-title">{{ $t('layout.footer.contact') }}</h4>
              <div class="contact-info">
                <div class="contact-item mb-2">
                  <v-icon small class="mr-2">mdi-email</v-icon>
                  <a href="mailto:info@asport.lv">info@asport.lv</a>
                </div>
                <div class="contact-item">
                  <v-icon small class="mr-2">mdi-map-marker</v-icon>
                  <span>{{ $t('layout.footer.contact-info.location') }}</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Bottom Bar -->
        <v-divider class="footer-divider mb-4"></v-divider>
        <v-row align="center">
          <v-col cols="12" md="6">
            <p class="copyright mb-0">
              {{ $t('layout.footer.copyright', { year: currentYear }) }}
            </p>
          </v-col>
          <v-col cols="12" md="6" class="text-left text-md-right">
            <div class="footer-meta">
              <span class="version-info">{{ $t('layout.footer.meta.version') }}</span>
              <span class="separator">•</span>
              <span>{{ $t('layout.footer.meta.made-in-latvia') }}</span>
              <v-icon small class="ml-1" color="#FF0000">mdi-heart</v-icon>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </footer>
</template>

<script>
import AppLogo from '@/components/Logo.vue'
import urlUtil from '@/js/utils/urlUtil'
import { EXAMPLE_STUDIO_IDENTIFIER, scrollToTopIfAlreadyOn } from '@/domains/website/websiteLinks'
import { openConsentSettings } from '@/domains/consent/trackingConsent'
import { MainPath } from '@/router/mainPaths'

export default {
  name: 'AppFooter',
  components: {
    AppLogo
  },
  data() {
    return {
      mainPath: MainPath,
      socialLinks: [
        /*{
          name: 'Facebook',
          icon: 'mdi-facebook',
          url: 'https://facebook.com/asport.lv'
        },
        {
          name: 'Instagram',
          icon: 'mdi-instagram',
          url: 'https://instagram.com/asport.lv'
        },
        {
          name: 'LinkedIn',
          icon: 'mdi-linkedin',
          url: 'https://linkedin.com/company/asport'
        },
        {
          name: 'YouTube',
          icon: 'mdi-youtube',
          url: 'https://youtube.com/@asportlv'
        }*/
      ]
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    exampleStudioUrl() {
      return urlUtil.getLocalisedOrganizationUrl({ identifier: EXAMPLE_STUDIO_IDENTIFIER })
    },
    homeUrl() {
      return urlUtil.getLocalisedUrl('/')
    }
  },
  methods: {
    /** A main-site page in the visitor's language, e.g. `/en/pricing`. */
    localisedPath(path) {
      return urlUtil.getLocalisedUrl('/' + path)
    },
    onLogoClick() {
      scrollToTopIfAlreadyOn(this.$route, this.homeUrl)
    },
    openCookieSettings() {
      openConsentSettings()
    },
    goToHome() {
      // Check if we're already on the home page
      if (urlUtil.isHomePage()) {
        // If already on home page, scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        // If on different page, navigate to home
        this.$router.push(urlUtil.getLocalisedUrl('/'))
      }
    }
  }
}
</script>

<style scoped>
.modern-footer {
  background: var(--gradient-header-footer);
  color: #ecf0f1;
  margin-top: auto;
}

.footer-content {
  padding: 4rem 0 2rem 0;
}

.footer-section {
  height: 100%;
}

.footer-logo {
  display: flex;
  align-items: center;
}

.footer-description {
  color: #bdc3c7;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 0;
}

.footer-title {
  color: #ecf0f1;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  position: relative;
}

.footer-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 30px;
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 0.75rem;
}

.footer-links a {
  color: white;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.footer-links a:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  padding-left: 4px;
}

.social-links {
  display: flex;
  gap: 0.5rem;
}

.social-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.social-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
}

.social-btn .v-icon {
  color: #ecf0f1 !important;
}

.contact-info {
  font-size: 0.9rem;
}

.contact-item {
  display: flex;
  align-items: center;
  color: white;
}

.contact-item a {
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-item a:hover {
  color: rgba(255, 255, 255, 0.8);
}

.contact-item .v-icon {
  color: white !important;
}

.footer-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
  margin: 2rem 0 1rem 0;
}

.copyright {
  color: #95a5a6;
  font-size: 0.85rem;
}

.footer-meta {
  color: #95a5a6;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

@media (min-width: 960px) {
  .footer-meta {
    justify-content: flex-end;
  }
}

.version-info {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.separator {
  margin: 0 0.75rem;
  opacity: 0.5;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .footer-content {
    padding: 3rem 0 1.5rem 0;
  }
  
  .footer-title {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  .footer-description {
    font-size: 0.9rem;
  }
  
  .footer-meta {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .separator {
    display: none;
  }
}

/* Dark background logo support */
.footer-logo :deep(.app-logo) {
  filter: brightness(1.2);
}
</style> 